import React, { useCallback, useEffect, useState } from 'react';
import { MessageSquare, Plus, Check, Pencil, Clock, User, Trash2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

function todayIso() {
  const n = new Date();
  const p = (x) => String(x).padStart(2, '0');
  return `${n.getFullYear()}-${p(n.getMonth() + 1)}-${p(n.getDate())}`;
}

function fmtDue(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('de-DE', {
    weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function fmtShort(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function splitDue(iso) {
  if (!iso) return { due_date: todayIso(), due_time: '09:00' };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { due_date: todayIso(), due_time: '09:00' };
  const p = (x) => String(x).padStart(2, '0');
  return {
    due_date: `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`,
    due_time: `${p(d.getHours())}:${p(d.getMinutes())}`,
  };
}

/**
 * Nachrichten/Erinnerungen für eine Person (Schüler, Lehrer, Zahler, Familie).
 */
export default function PersonRemindersPanel({
  personType,
  personId,
  personLabel,
  compact = false,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeDone, setIncludeDone] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ due_date: todayIso(), due_time: '09:00', text: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!personType || !personId) return;
    setLoading(true);
    try {
      const r = await api.get('/person-reminders', {
        params: { person_type: personType, person_id: personId, include_done: includeDone, limit: 100 },
      });
      setItems(r.data?.items || []);
    } catch {
      toast.error('Erinnerungen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [personType, personId, includeDone]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({ due_date: todayIso(), due_time: '09:00', text: '' });
    setEditorOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ ...splitDue(item.due_at), text: item.text || '' });
    setEditorOpen(true);
  };

  const save = async () => {
    if (!form.text.trim()) {
      toast.error('Bitte einen Text eingeben');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/person-reminders/${editing.id}`, {
          due_date: form.due_date,
          due_time: form.due_time,
          text: form.text.trim(),
        });
        toast.success('Erinnerung gespeichert');
      } else {
        await api.post('/person-reminders', {
          person_type: personType,
          person_id: personId,
          due_date: form.due_date,
          due_time: form.due_time,
          text: form.text.trim(),
        });
        toast.success('Erinnerung angelegt');
      }
      setEditorOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const complete = async (item) => {
    try {
      await api.post(`/person-reminders/${item.id}/complete`);
      toast.success('Als erledigt markiert');
      load();
    } catch {
      toast.error('Konnte nicht abgehakt werden');
    }
  };

  const remove = async (item) => {
    if (!window.confirm('Erinnerung wirklich löschen?')) return;
    try {
      await api.delete(`/person-reminders/${item.id}`);
      toast.success('Gelöscht');
      load();
    } catch {
      toast.error('Löschen fehlgeschlagen');
    }
  };

  return (
    <div className={compact ? '' : 'bg-white rounded-xl border border-slate-200 shadow-sm p-6'} data-testid="person-reminders-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-brand-red" />
          <h2 className="text-lg font-semibold text-slate-900">Nachrichten / Erinnerungen</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={includeDone}
              onChange={(e) => setIncludeDone(e.target.checked)}
              className="accent-brand-red"
            />
            Erledigte anzeigen
          </label>
          <Button
            type="button"
            size="sm"
            className="bg-brand-red hover:bg-brand-red/90"
            onClick={openNew}
            data-testid="reminder-new-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Neuen Termin hinzufügen
          </Button>
        </div>
      </div>

      {personLabel && (
        <p className="text-xs text-slate-500 mb-3">Person: <strong className="text-slate-700">{personLabel}</strong></p>
      )}

      {loading ? (
        <p className="text-sm text-slate-400 py-8 text-center">Laden…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-500 py-8 text-center">
          Noch keine Erinnerungen. Legen Sie den ersten Termin an.
        </p>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <div
              key={item.id}
              className={`rounded-lg border border-slate-200 bg-white p-3 shadow-sm ${item.is_done ? 'opacity-60' : ''}`}
              data-testid={`reminder-card-${item.id}`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-1.5 text-xs text-slate-500">
                  <Clock className="w-3.5 h-3.5" />
                  <span className="font-medium text-slate-700">{fmtDue(item.due_at)}</span>
                </div>
                <div className="flex items-center gap-1">
                  {!item.is_done && (
                    <button
                      type="button"
                      title="Erledigt"
                      onClick={() => complete(item)}
                      className="p-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700"
                      data-testid={`reminder-complete-${item.id}`}
                    >
                      <Check className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button
                    type="button"
                    title="Bearbeiten"
                    onClick={() => openEdit(item)}
                    className="p-1.5 rounded bg-slate-700 text-white hover:bg-slate-800"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    title="Löschen"
                    onClick={() => remove(item)}
                    className="p-1.5 rounded border border-slate-200 text-slate-500 hover:text-brand-red hover:border-brand-red/30"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <p className="text-sm text-slate-800 whitespace-pre-wrap">{item.text}</p>
              {item.link_label && (
                <p className="text-xs text-brand-red mt-1">{item.link_label}</p>
              )}
              <div className="mt-2 pt-2 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-500">
                <span className="inline-flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {item.updated_by_name || item.created_by_name || '—'}
                </span>
                <span>
                  {item.is_done
                    ? `erledigt: ${fmtShort(item.done_at)}`
                    : `zuletzt geändert: ${fmtShort(item.updated_at || item.created_at)}`}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-lg" data-testid="reminder-editor-dialog">
          <DialogHeader>
            <DialogTitle>
              {editing ? 'Termin bearbeiten' : 'Neuen Termin hinzufügen'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Datum</label>
                <Input
                  type="date"
                  value={form.due_date}
                  onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))}
                  data-testid="reminder-due-date"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Uhrzeit</label>
                <Input
                  type="time"
                  value={form.due_time}
                  onChange={(e) => setForm((f) => ({ ...f, due_time: e.target.value }))}
                  data-testid="reminder-due-time"
                />
              </div>
            </div>
            {personLabel && (
              <div className="text-sm text-slate-600 flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                <User className="w-4 h-4 text-brand-red" />
                <span>{personLabel}</span>
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Text</label>
              <textarea
                value={form.text}
                onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
                rows={5}
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-red/30"
                placeholder="Korrespondenz, ToDo, Wiedervorlage…"
                data-testid="reminder-text"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setEditorOpen(false)} disabled={saving}>
              Abbrechen
            </Button>
            <Button
              type="button"
              className="bg-brand-red hover:bg-brand-red/90"
              onClick={save}
              disabled={saving}
              data-testid="reminder-save-btn"
            >
              {saving ? 'Speichern…' : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Kompakte Ops-Karte fürs Startfenster */
export function ReminderOpsCard({ item, onComplete, onOpen, urgency = 'week' }) {
  const [expanded, setExpanded] = useState(false);
  const text = item.text || '';
  const long = text.length > 120;
  const shown = expanded || !long ? text : `${text.slice(0, 120)}…`;
  const isToday = urgency === 'today';
  const bar = urgency === 'overdue'
    ? 'border-l-4 border-l-brand-red'
    : isToday
      ? 'border-l-4 border-l-transparent'
      : 'border-l-4 border-l-slate-400';

  return (
    <div
      className={`relative w-full text-left bg-white rounded-lg border border-slate-200 ${bar} px-3 py-2.5 shadow-sm`}
      data-testid={`ops-reminder-${item.id}`}
      data-urgency={urgency}
    >
      {isToday && (
        <span
          data-testid="ops-urgency-dot"
          className="absolute left-2.5 top-3 h-2 w-2 rounded-full bg-brand-red"
          aria-hidden
        />
      )}
      <div className={isToday ? 'pl-3' : undefined}>
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {fmtDue(item.due_at || item.date)}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              title="Erledigt"
              onClick={(e) => { e.stopPropagation(); onComplete?.(item); }}
              className="p-1 rounded bg-emerald-600 text-white hover:bg-emerald-700"
              data-testid={`ops-reminder-complete-${item.id}`}
            >
              <Check className="w-3 h-3" />
            </button>
            <button
              type="button"
              title="Zur Person"
              onClick={(e) => { e.stopPropagation(); onOpen?.(item); }}
              className="p-1 rounded bg-slate-700 text-white hover:bg-slate-800"
            >
              <ExternalLink className="w-3 h-3" />
            </button>
          </div>
        </div>
        <div className="text-sm font-semibold text-slate-800 leading-snug">
          {item.person_name || item.title}
        </div>
        <div className="text-xs text-slate-600 mt-0.5 whitespace-pre-wrap">{shown}</div>
        {long && (
          <button
            type="button"
            className="text-[11px] text-brand-red mt-0.5"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? '∧ weniger' : '∨ mehr'}
          </button>
        )}
        <div className="mt-1.5 pt-1.5 border-t border-slate-100 flex justify-between gap-2 text-[10px] text-slate-400">
          <span>{item.created_by_name || item.updated_by_name || '—'}</span>
          <span>{fmtShort(item.updated_at || item.created_at)}</span>
        </div>
      </div>
    </div>
  );
}
